// /src/components/core/rich-textarea.tsx
'use client';

import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Dialog, DialogContent } from '@ui/core/dialog';
import { useMediaQuery } from '@ui/lib/use-media-query';
import { cn } from '@ui/lib/utils';
import { ChevronLeft, Expand, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { FloatingToolbar } from './rich-textarea/floating-toolbar';

// --- Interface ---
export interface RichTextareaProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
  minHeight?: string;
  minimizeButtonLabel?: string;
  minimizeButtonIcon?: React.ReactNode;
  focusModeIcon?: React.ReactNode;
  onFocusModeClick?: () => void;
  focusModeLabel?: string;
  fieldTitle?: React.ReactNode;
  savingBadge?: React.ReactNode;
  lastUpdatedBadge?: React.ReactNode;
  onSave?: () => void;
  saveButtonLabel?: string;
  onDiscard?: () => void;
  discardButtonLabel?: string;
  showRichPlaceholder?: boolean;
  autoFocus?: boolean;
  disablePopOut?: boolean;
  /**
   * Ref to the underlying contentEditable (for focusing programmatically)
   */
  textareaRef?: React.RefObject<HTMLDivElement | null>;
}

export const RichTextarea = React.forwardRef<HTMLDivElement, RichTextareaProps>(
  (
    {
      value = '',
      onChange,
      className,
      placeholder,
      minHeight = '120px',
      minimizeButtonLabel = 'Exit Focus Mode',
      minimizeButtonIcon = <ChevronLeft className="h-3.5 w-3.5" />,
      focusModeIcon = <Expand className="h-3.5 w-3.5" />,
      onFocusModeClick,
      focusModeLabel = 'Focus Mode',
      fieldTitle,
      savingBadge,
      lastUpdatedBadge,
      onSave,
      saveButtonLabel = 'Save & Publish',
      onDiscard,
      discardButtonLabel = 'Discard',
      showRichPlaceholder = false,
      autoFocus = false,
      disablePopOut = false,
      textareaRef,
    },
    ref
  ) => {
    const [isPopoutOpen, setIsPopoutOpen] = useState(false);
    const [editorPosition, setEditorPosition] = useState({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    });
    const editorRef = useRef<HTMLDivElement>(null);
    const [showingPlaceholder, setShowingPlaceholder] =
      useState(showRichPlaceholder);
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const userInitiatedInteraction = useRef(false);

    // Touch tracking to distinguish scroll vs tap intent
    const touchTrackingRef = useRef<{
      startPos: { x: number; y: number } | null;
      moved: boolean;
    }>({
      startPos: null,
      moved: false,
    });

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ heading: false, strike: false }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: 'text-blue-600 underline' },
        }),
        Underline,
        Strike,
        Heading.configure({ levels: [1, 2, 3] }),
        TaskList,
        TaskItem.configure({ nested: true }),
      ],
      content: value,
      onUpdate: ({ editor }) => {
        onChange?.(editor.getHTML());
        if (showingPlaceholder) setShowingPlaceholder(false);
      },
      onFocus: () => {
        if (showingPlaceholder) setShowingPlaceholder(false);
      },
      editorProps: {
        attributes: {
          class:
            'prose prose-slate max-w-none dark:prose-invert prose-headings:font-display focus:outline-none h-full min-h-full text-rendering-optimizeLegibility antialiased',
          inputMode: 'text',
          role: 'textbox', // ← treat as textarea
          'aria-multiline': 'true', // ← multiline support
          tabIndex: '0', // ← allow focus()
        },
      },
      immediatelyRender: false,
    });

    // Sync external value into editor
    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value, false);
      }
    }, [editor, value]);

    const isEmpty = editor?.isEmpty ?? true;

    // Placeholder logic
    useEffect(() => {
      if (showRichPlaceholder && isEmpty && editor && !isPopoutOpen) {
        setShowingPlaceholder(true);
      } else {
        setShowingPlaceholder(false);
      }
    }, [isEmpty, editor, showRichPlaceholder, isPopoutOpen]);

    // Track editor position
    useEffect(() => {
      if (editorRef.current && !isPopoutOpen) {
        const rect = editorRef.current.getBoundingClientRect();
        setEditorPosition({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    }, [isPopoutOpen]);

    // Attach click/touch handlers on main container
    useEffect(() => {
      if (!editor) return;
      const editorEl = editor.view.dom as HTMLElement;
      editorEl.style.minHeight = 'inherit';
      editorEl.style.height = '100%';
      editorEl.style.cursor = 'text';
      editorEl.style.touchAction = 'auto';

      const container = editorEl.closest('.rich-textarea-container');
      if (container) {
        const onClick = () => {
          userInitiatedInteraction.current = true;
          if (!editor.isFocused) editor.commands.focus();
          if (showingPlaceholder) setShowingPlaceholder(false);
        };

        // Touch movement detection to prevent accidental focus during scrolling
        const TOUCH_MOVEMENT_THRESHOLD = 10; // pixels

        const onTouchStart = (e: Event) => {
          const touchEvent = e as TouchEvent;
          const touch = touchEvent.touches?.[0];
          if (!touch) return;

          touchTrackingRef.current = {
            startPos: {
              x: touch.clientX,
              y: touch.clientY,
            },
            moved: false,
          };
        };

        const onTouchMove = (e: Event) => {
          const touchEvent = e as TouchEvent;
          const touchTracking = touchTrackingRef.current;
          if (!touchTracking.startPos) return;

          const touch = touchEvent.touches?.[0];
          if (!touch) return;

          const deltaX = Math.abs(touch.clientX - touchTracking.startPos.x);
          const deltaY = Math.abs(touch.clientY - touchTracking.startPos.y);

          if (
            deltaX > TOUCH_MOVEMENT_THRESHOLD ||
            deltaY > TOUCH_MOVEMENT_THRESHOLD
          ) {
            touchTracking.moved = true;
          }
        };

        const onTouchEnd = (e: Event) => {
          const touchTracking = touchTrackingRef.current;

          // Only focus if this was a stationary tap, not a scroll gesture
          if (!touchTracking.moved && touchTracking.startPos) {
            userInitiatedInteraction.current = true;

            // Don't prevent default - let TipTap handle the event properly
            // Use setTimeout to allow TipTap's internal processing to complete
            if (!editor.isFocused) {
              setTimeout(() => {
                try {
                  // Only focus if the editor is still available and not destroyed
                  if (editor && !editor.isDestroyed && !editor.isFocused) {
                    editor.commands.focus();
                  }
                } catch (error) {
                  // Silently handle any TipTap state errors
                  console.debug('RichTextarea focus error:', error);
                }
              }, 0);
            }

            if (showingPlaceholder) setShowingPlaceholder(false);
          }

          // Reset touch tracking
          touchTrackingRef.current = {
            startPos: null,
            moved: false,
          };
        };

        container.addEventListener('click', onClick);
        container.addEventListener('touchstart', onTouchStart, {
          passive: true,
        });
        container.addEventListener('touchmove', onTouchMove, { passive: true });
        container.addEventListener('touchend', onTouchEnd, { passive: true }); // Now passive since no preventDefault

        return () => {
          container.removeEventListener('click', onClick);
          container.removeEventListener('touchstart', onTouchStart);
          container.removeEventListener('touchmove', onTouchMove);
          container.removeEventListener('touchend', onTouchEnd);
        };
      }
    }, [editor, showingPlaceholder]);

    // Handle autoFocus prop - modified to be more strict about never auto-focusing
    useEffect(() => {
      if (editor && autoFocus) {
        // Only focus if explicitly requested via autoFocus AND no scrolling/layout is happening
        const timeoutId = setTimeout(() => {
          // Safety checks to prevent unwanted focus
          if (
            document.activeElement &&
            document.activeElement !== document.body
          ) {
            return;
          }

          // Don't auto-focus during initial page load or if not explicitly requested
          if (!userInitiatedInteraction.current) {
            return;
          }

          editor.commands.focus('start');
        }, 300); // Increased delay to ensure page is stable

        return () => clearTimeout(timeoutId);
      }
    }, [editor, autoFocus]);

    // Focus editor when popout opens - but only when user explicitly clicked to open it
    useEffect(() => {
      if (isPopoutOpen && editor && userInitiatedInteraction.current) {
        const id = setTimeout(() => {
          editor.commands.focus('end');
          try {
            editor.view.dom.focus();
          } catch {}
        }, 200);
        return () => clearTimeout(id);
      }
    }, [isPopoutOpen, editor]);

    // Refresh editor when popout closes - modified to prevent auto-focus
    useEffect(() => {
      if (!isPopoutOpen && editor) {
        const html = editor.getHTML();
        setTimeout(() => {
          try {
            editor.view.updateState(editor.view.state);
            if (editor.isEmpty && html && html !== '<p></p>') {
              editor.commands.setContent(html);
            }
            // Remove focus commands that cause scrolling
            // editor.commands.focus();
            // setTimeout(() => editor.commands.blur(), 10);
          } catch {}
        }, 100);
      }
    }, [isPopoutOpen, editor]);

    // *** POP-OUT HANDLER (unchanged) ***
    const handlePopOut = () => {
      if (!isDesktop) return; // Don't trigger popout on non-desktop devices

      userInitiatedInteraction.current = true;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      let proxy: HTMLTextAreaElement | null = null;

      if (isIOS) {
        proxy = document.createElement('textarea');
        proxy.setAttribute('aria-hidden', 'true');
        Object.assign(proxy.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '1px',
          height: '1px',
          opacity: '0',
          fontSize: '16px',
        });
        document.body.appendChild(proxy);
        proxy.focus();
      }

      setIsPopoutOpen(true);

      if (isIOS && proxy && proxy.parentNode) {
        proxy.parentNode.removeChild(proxy);
      }

      if (editor) {
        editor.commands.focus('end');
        try {
          editor.view.dom.focus();
        } catch {}
      }

      if (onFocusModeClick) {
        onFocusModeClick();
      }
    };

    const handlePopIn = () => setIsPopoutOpen(false);
    const handleEditorClick = () => {
      userInitiatedInteraction.current = true;
      if (showingPlaceholder && editor) {
        setShowingPlaceholder(false);
        editor.commands.focus('start');
      }
    };

    return (
      <>
        {/* ---------- Main editor ---------- */}
        <div
          ref={(node) => {
            if (typeof ref === 'function') ref(node as HTMLDivElement);
            else if (ref) ref.current = node as HTMLDivElement;
            editorRef.current = node;
          }}
          className={cn(
            'relative border border-slate-200/50 rounded-lg shadow-sm rich-textarea-container w-full',
            className
          )}
          style={{ minHeight }}
        >
          <div
            className="p-2 sm:p-3 pb-1 relative"
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight,
            }}
          >
            {placeholder && isEmpty && !showingPlaceholder && (
              <div className="absolute top-2 sm:top-3 left-2 sm:left-3 pointer-events-none text-slate-600 italic opacity-70 text-sm sm:text-base w-[calc(100%-2rem)]">
                {placeholder}
              </div>
            )}

            {showingPlaceholder && isEmpty && (
              <div
                className="absolute top-2 sm:top-3 left-2 sm:left-3 text-slate-600 opacity-70 pointer-events-none w-[calc(100%-2rem)]"
                onClick={handleEditorClick}
              >
                <div className="prose prose-slate max-w-none space-y-2">
                  <h2 className="text-lg font-bold text-slate-600 m-0">
                    Heading example
                  </h2>
                  <p className="m-0">
                    This is what a paragraph would look like. Start typing to
                    begin…
                  </p>
                  <ul className="m-0 pl-5">
                    <li>Bullet point item 1</li>
                    <li>Bullet point item 2</li>
                  </ul>
                  <ol className="m-0 pl-5">
                    <li>Numbered list item 1</li>
                    <li>Numbered list item 2</li>
                  </ol>
                </div>
              </div>
            )}

            <EditorContent
              editor={editor}
              className={cn(
                'tiptap-editor flex-grow min-h-[inherit] relative z-10 h-full',
                isEmpty && 'empty-editor'
              )}
              style={{
                minHeight: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                textRendering: 'optimizeLegibility',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                fontSize: 'inherit',
              }}
              onClick={showingPlaceholder ? handleEditorClick : undefined}
              ref={textareaRef}
            />

            {editor && (
              <div className="mt-2 overflow-x-auto">
                <FloatingToolbar
                  editor={editor}
                  onPopOut={
                    disablePopOut
                      ? undefined
                      : isDesktop
                        ? handlePopOut
                        : undefined
                  }
                  isExpanded={false}
                  focusModeIcon={focusModeIcon}
                  focusModeLabel={focusModeLabel}
                />
              </div>
            )}
          </div>
        </div>

        {/* ---------- Popout dialog ---------- */}
        {isDesktop && (
          <Dialog open={isPopoutOpen} onOpenChange={setIsPopoutOpen}>
            <DialogContent
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="max-w-[95vw] md:max-w-[800px] max-h-[90vh] md:max-h-[70vh] w-full h-[90vh] md:h-[70vh] p-0 overflow-hidden border-none rounded-lg md:rounded-xl shadow-2xl"
              style={{
                transformOrigin: 'center center',
                backgroundColor: 'white',
                touchAction: 'auto',
                zIndex: 9999,
              }}
            >
              <div className="flex flex-col h-full w-full">
                {/* Header */}
                <div className="border-b border-slate-200/50 p-4 bg-white flex items-center justify-between shadow-sm">
                  <span className="text-slate-800 font-bold">
                    {fieldTitle || 'Pop-out Editor'}
                  </span>
                  <button
                    onClick={() => setIsPopoutOpen(false)}
                    className="p-2 text-slate-600 hover:text-slate-700 hidden"
                    aria-label="Close editor"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Editor area */}
                <div
                  className="flex-grow overflow-auto"
                  style={{
                    position: 'relative',
                    WebkitOverflowScrolling: 'touch',
                    background: 'white',
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                  }}
                >
                  {editor && (
                    <div
                      className="h-full w-full p-4"
                      onTouchStart={() => {
                        userInitiatedInteraction.current = true;
                        if (editor && !editor.isFocused) {
                          try {
                            editor.commands.focus();
                          } catch {}
                        }
                      }}
                      onClick={() => {
                        userInitiatedInteraction.current = true;
                        if (editor && !editor.isFocused) {
                          try {
                            editor.commands.focus();
                            editor.view.dom.focus();
                          } catch {}
                        }
                      }}
                    >
                      {showingPlaceholder && isEmpty && (
                        <div className="absolute left-4 top-4 text-slate-600 opacity-70 pointer-events-none z-10">
                          <div className="prose prose-slate max-w-none space-y-2">
                            <h2 className="text-lg font-bold text-slate-600 m-0">
                              Tap to start typing…
                            </h2>
                            <p className="m-0">
                              This editor supports formatting options like:
                            </p>
                            <ul className="m-0 pl-5">
                              <li>Bullet points</li>
                              <li>Headings &amp; text styles</li>
                            </ul>
                          </div>
                        </div>
                      )}
                      <EditorContent
                        editor={editor}
                        className="h-full w-full mobile-editor"
                        style={{
                          minHeight: '100%',
                          fontSize: '16px',
                          lineHeight: '1.5',
                          position: 'relative',
                          zIndex: 20,
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'auto',
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                        }}
                        onClick={(e) => {
                          userInitiatedInteraction.current = true;
                          if (showingPlaceholder) setShowingPlaceholder(false);
                          if (editor && !editor.isFocused) {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              editor.commands.focus();
                              editor.view.dom.focus();
                            } catch {}
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Bottom toolbar */}
                <div
                  className="px-2 py-2 border-t border-slate-200 overflow-x-auto"
                  style={{ backgroundColor: 'white' }}
                >
                  {editor && (
                    <FloatingToolbar
                      editor={editor}
                      isExpanded={true}
                      minimizeButtonLabel={minimizeButtonLabel}
                      minimizeButtonIcon={minimizeButtonIcon}
                      onMinimize={handlePopIn}
                      focusModeIcon={focusModeIcon}
                      focusModeLabel={focusModeLabel}
                    />
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }
);

RichTextarea.displayName = 'RichTextarea';
export default RichTextarea;
